import { Integration, Event, Primitive } from "@jupiter/types";
import { isPrimitive, isErrorEvent, getGlobalObject } from "@jupiter/utils";
import { getCurrentHub } from "@jupiter/hub";
import isString from "lodash-es/isString";
import { EventBuilder } from "@jupiter/event";

interface GlobalHandlersIntegrations {
  onerror: boolean;
  onunhandledrejection: boolean;
}

const global = getGlobalObject<Window>();

export class GlobalHandlers implements Integration {
  /**
   * @inheritDoc
   */
  public static id: string = "GlobalHandlers";

  /**
   * @inheritDoc
   */
  public name: string = GlobalHandlers.id;

  /** JSDoc */
  private readonly _options: GlobalHandlersIntegrations;

  /** JSDoc */
  private _onErrorHandlerInstalled: boolean = false;

  /** JSDoc */
  private _onUnhandledRejectionHandlerInstalled: boolean = false;

  /** JSDoc */
  public constructor(options?: GlobalHandlersIntegrations) {
    this._options = {
      onerror: true,
      onunhandledrejection: true,
      ...options,
    };
  }

  /**
   * @inheritDoc
   */
  public setupOnce(): void {
    if (this._options.onerror) {
      this._installGlobalOnErrorHandler();
    }

    if (this._options.onunhandledrejection) {
      this._installGlobalOnUnhandledRejectionHandler();
    }
  }

  /** JSDoc */
  private _installGlobalOnErrorHandler(): void {
    if (this._onErrorHandlerInstalled) {
      return;
    }

    const _oldOnErrorHandler = global.onerror;
    const that = this;
    global.onerror = function (
      msg: any,
      url: any,
      line: any,
      column: any,
      error: any
    ) {
      const currentHub = getCurrentHub();
      const hasIntegration = currentHub.getIntegration(GlobalHandlers);

      if (!hasIntegration) {
        return false;
      }

      const { stack, message } = error;

      const event = isPrimitive(error)
        ? that._eventFromIncompleteOnError(msg, url, line, column)
        : EventBuilder.createExceptionEvent(stack, { message });

      currentHub.track(event, {
        originalException: error,
      });

      if (_oldOnErrorHandler) {
        return _oldOnErrorHandler.apply(this, [msg, url, line, column, error]);
      }

      return false;
    };

    this._onErrorHandlerInstalled = true;
  }

  /** JSDoc */
  private _installGlobalOnUnhandledRejectionHandler(): void {
    if (this._onUnhandledRejectionHandlerInstalled) {
      return;
    }

    const _oldOnUnhandledRejectionHandler = global.onunhandledrejection;
    const that = this;
    global.onunhandledrejection = function (e: any): boolean {
      let error = e;

      // dig the object of the rejection out of known event types
      try {
        if ("reason" in e) {
          error = e.reason;
        } else if ("detail" in e && "reason" in e.detail) {
          error = e.detail.reason;
        }
      } catch (_oO) {
        // no-empty
      }

      const currentHub = getCurrentHub();
      const hasIntegration = currentHub.getIntegration(GlobalHandlers);

      if (!hasIntegration) {
        return true;
      }

      const { stack, message } = error;

      const event = isPrimitive(error)
        ? that._eventFromRejectionWithPrimitive(error)
        : EventBuilder.createExceptionEvent(stack, { message });

      currentHub.track(event, {
        originalException: error,
      });

      if (_oldOnUnhandledRejectionHandler) {
        return _oldOnUnhandledRejectionHandler.apply(this, [e]);
      }

      return true;
    };

    this._onUnhandledRejectionHandlerInstalled = true;
  }

  /**
   * This function creates a stack from an old, error-less onerror handler.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _eventFromIncompleteOnError(
    msg: any,
    url: any,
    line: any,
    column: any
  ): Event {
    const ERROR_TYPES_RE = /^(?:[Uu]ncaught (?:exception: )?)?(?:((?:Eval|Internal|Range|Reference|Syntax|Type|URI|)Error): )?(.*)$/i;

    // If 'message' is ErrorEvent, get real message from inside
    let message = isErrorEvent(msg) ? msg.message : msg;
    let name;

    if (isString(message)) {
      const groups = message.match(ERROR_TYPES_RE);
      if (groups) {
        name = groups[1];
        message = groups[2];
      }
    }

    return EventBuilder.createExceptionEvent(message, {
      name,
      line,
      column,
      fullUrl: url,
    });
  }

  private _eventFromRejectionWithPrimitive(reason: Primitive): Event {
    return EventBuilder.createExceptionEvent(reason);
  }
}
